import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Clock, 
  BookOpen, 
  Sparkles, 
  Users, 
  Calendar, 
  Video, 
  Shield,
  Play,
  Heart,
  Compass,
  Sun,
  MessageCircle,
  ChevronRight,
  Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { scrollToSection } from '../hooks/useSmoothScroll';

const Formaciones = () => {
  const [courses, setCourses] = useState([]);
  const [specialCourses, setSpecialCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('todos');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const [coursesRes, specialRes] = await Promise.all([
          api.get('/courses'),
          api.get('/special-courses').catch(() => ({ data: [] }))
        ]);
        setCourses(coursesRes.data);
        setSpecialCourses(specialRes.data || []);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  // Filter courses by category
  const filteredCourses = activeFilter === 'todos' 
    ? courses 
    : courses.filter(c => c.category === activeFilter);

  // Category definitions
  const categories = [
    { id: 'todos', label: 'Todos', icon: BookOpen },
    { id: 'fundamentos', label: 'Fundamentos', icon: Compass },
    { id: 'sanacion', label: 'Sanación', icon: Heart },
    { id: 'luz', label: 'Luz y Consciencia', icon: Sun }
  ];

  // Icon mapping for dynamic rendering
  const iconMap = {
    BookOpen, Calendar, Users, Video, Clock, Heart, Shield, Sparkles, Play
  };

  // Render a special course card
  const renderSpecialCourseCard = (course, index) => {
    const card = course.card_config || {};
    const hero = course.landing_page_content?.hero || {};
    const pricing = course.landing_page_content?.pricing || {};

    // Defensive defaults: pull from top-level fields when card_config is empty
    const gradientFrom = card.gradient_from || '#e8eaed';
    const gradientTo = card.gradient_to || '#f8f9fa';
    const textColor = card.text_color || 'dark';
    const imageUrl = card.image_url || course.image_url || hero.background_image || 'https://res.cloudinary.com/dpppkdhdk/image/upload/f_auto,q_auto,w_1200/v1776874282/landing_limites_oxeqbv.png';
    const imagePosition = card.image_position || 'left';
    const features = (card.features && card.features.length) ? card.features : (hero.features || []);
    const badgeEdition = card.badge_edition || hero.badge_text;
    const badgeStatus = card.badge_status || 'INSCRIPCIONES ABIERTAS';
    // Price fallback: card_config > pricing.pronto_pago > top-level price formatted
    const priceDisplay = card.price_display
      || pricing.pronto_pago?.price
      || (course.price ? `${Math.round(course.price)}€` : '');
    const priceNote = card.price_note || 'pago único';
    const ctaText = card.cta_text || 'Reservar mi plaza';

    const isLightTheme = textColor !== 'white';
    const textPrimary = isLightTheme ? 'text-[#505560]' : 'text-white';
    const textSecondary = isLightTheme ? 'text-[#606468]/80' : 'text-white/80';
    const textMuted = isLightTheme ? 'text-[#606468]/70' : 'text-white/70';
    const badgeBg = isLightTheme ? 'bg-[#606468]/10 text-[#606468]' : 'bg-white/20 text-white';

    return (
      <div 
        key={course.id}
        className={`relative rounded-3xl overflow-hidden shadow-xl ${isLightTheme ? 'border border-gray-100' : ''}`}
        style={{ 
          background: isLightTheme 
            ? `linear-gradient(to bottom right, #e8eaed, #f0f2f5, #f8f9fa)`
            : `linear-gradient(to bottom right, ${gradientFrom}, ${gradientTo})`
        }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Content - order changes based on image position */}
          <div className={`p-8 sm:p-12 flex flex-col justify-center ${imagePosition === 'left' ? 'order-2' : 'order-2 lg:order-1'}`}>
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mb-6">
              <span className={`inline-flex items-center gap-1.5 ${badgeBg} text-xs font-medium px-3 py-1.5 rounded-full backdrop-blur-sm`}>
                <Video className="w-3.5 h-3.5" />
                EN VIVO
              </span>
              {badgeEdition && (
                <span className="inline-flex items-center gap-1.5 bg-[hsl(var(--accent))] text-white text-xs font-medium px-3 py-1.5 rounded-full">
                  {badgeEdition}
                </span>
              )}
              <span className={`inline-flex items-center gap-1.5 ${isLightTheme ? 'bg-red-50 text-red-600' : 'bg-white/10 text-white/90'} text-xs font-medium px-3 py-1.5 rounded-full`}>
                {badgeStatus}
              </span>
            </div>
            
            <h3 className={`text-2xl sm:text-3xl lg:text-4xl font-heading ${textPrimary} mb-3`}>
              {course.title}
            </h3>
            <p className={`${textMuted} mb-6 leading-relaxed`}>
              {course.description}
            </p>
            
            {/* Features */}
            {features.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                {features.map((feature, i) => {
                  const IconComponent = iconMap[feature.icon] || BookOpen;
                  return (
                    <div key={i} className={`flex items-center gap-2 ${textSecondary} text-sm`}>
                      <IconComponent className="w-4 h-4 flex-shrink-0" />
                      <span>{feature.text}</span>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link to={`/formaciones/${course.slug}`} data-testid={`special-course-link-${index}`}>
                <Button 
                  className={`${isLightTheme ? 'bg-[#c9a227] text-white hover:bg-[#b89220]' : `bg-white text-[${gradientFrom}] hover:bg-white/90`} rounded-full px-6 py-3 font-medium shadow-lg`}
                  style={!isLightTheme ? { color: gradientFrom } : {}}
                >
                  {ctaText}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              {priceDisplay && (
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl sm:text-3xl font-heading ${isLightTheme ? 'text-[#c9a227]' : 'text-white'}`}>
                    {priceDisplay}
                  </span>
                  {priceNote && (
                    <span className={`text-sm ${isLightTheme ? 'text-[#606468]/50' : 'text-white/50'}`}>
                      {priceNote}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Image */}
          <div className={`relative h-64 lg:h-auto lg:min-h-[480px] ${imagePosition === 'left' ? 'order-1' : 'order-1 lg:order-2'}`}>
            {imageUrl && (
              <img 
                src={imageUrl}
                alt={course.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-20">
      {/* ============ HERO ============ */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-white to-[hsl(var(--sage-light))]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[hsl(var(--accent))]/10 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-[hsl(var(--accent))]" />
              <span className="text-sm text-[hsl(var(--accent))] font-medium">Escuela de Consciencia Akáshica</span>
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[hsl(var(--text-primary))] mb-6 leading-tight"
              data-testid="formaciones-title"
            >
              Formaciones para habitar tu consciencia
            </h1>
            <p className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed mb-10">
              Elige el formato que mejor se adapte a ti: formaciones en vivo con acompañamiento 
              grupal o cursos grabados para avanzar a tu ritmo.
            </p>
            
            {/* Quick navigation */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => scrollToSection('en-vivo')}
                className="inline-flex items-center justify-center gap-2 bg-[hsl(var(--accent))] text-white px-8 py-4 rounded-full font-medium hover:bg-[hsl(var(--accent))]/90 transition-all shadow-lg hover:shadow-xl"
              >
                <Video className="w-5 h-5" />
                Formaciones en vivo
                <ChevronRight className="w-4 h-4" />
              </button>
              <button 
                onClick={() => scrollToSection('grabados')}
                className="inline-flex items-center justify-center gap-2 bg-white text-[hsl(var(--text-primary))] px-8 py-4 rounded-full font-medium border-2 border-[hsl(var(--border))] hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))] transition-all"
              >
                <Play className="w-5 h-5" />
                Cursos a tu ritmo
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FORMACIONES EN VIVO ============ */}
      <section id="en-vivo" className="py-16 sm:py-20 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[hsl(var(--accent))] to-[hsl(var(--accent))]/80 flex items-center justify-center">
                <Video className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">
                  Formaciones en vivo
                </h2>
                <p className="text-sm text-[hsl(var(--text-secondary))]">
                  Acompañamiento grupal · Fechas específicas · Plazas limitadas
                </p>
              </div>
            </div>
          </div>

          {/* Live Courses Grid - Dynamic */}
          <div className="space-y-8">
            {specialCourses.length === 0 ? (
              <div className="text-center py-12 bg-[hsl(var(--sage-light))]/30 rounded-2xl">
                <Video className="w-12 h-12 text-[hsl(var(--sage))] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[hsl(var(--text-secondary))]">
                  No hay formaciones en vivo disponibles en este momento.
                </p>
              </div>
            ) : (
              specialCourses.map((course, index) => renderSpecialCourseCard(course, index))
            )}
          </div>
        </div>
      </section>

      {/* ============ CURSOS GRABADOS ============ */}
      <section id="grabados" className="py-16 sm:py-20 bg-[hsl(var(--sage-light))]/50 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[hsl(var(--sage))] to-[hsl(var(--sage))]/80 flex items-center justify-center">
                <Play className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))]">
                  Cursos grabados
                </h2>
                <p className="text-sm text-[hsl(var(--text-secondary))]">
                  A tu ritmo · Acceso inmediato · Para siempre
                </p>
              </div>
            </div>
            
            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveFilter(cat.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeFilter === cat.id
                        ? 'bg-[hsl(var(--accent))] text-white shadow-md'
                        : 'bg-white text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--accent))]/10 hover:text-[hsl(var(--accent))]'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Courses Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-[hsl(var(--text-secondary))]">Cargando cursos...</p>
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-[hsl(var(--sage))] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[hsl(var(--text-secondary))]">
                No hay cursos en esta categoría.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCourses.map((course, index) => {
                const isAvailable = course.availability_status === 'disponible' || !course.availability_status;
                const isComingSoon = course.availability_status === 'proximamente';
                const isNotAvailable = course.availability_status === 'no_disponible';
                const showPrice = !course.hide_price;
                
                return (
                  <Link 
                    key={course.id} 
                    to={isAvailable ? `/formaciones/${course.id}` : '#'}
                    className={`group block ${!isAvailable ? 'cursor-default' : ''}`}
                    data-testid={`course-card-${index}`}
                    onClick={(e) => !isAvailable && e.preventDefault()}
                  >
                    <div className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col ${isAvailable ? 'hover:-translate-y-1' : 'opacity-90'}`}>
                      {/* Image */}
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img 
                          src={course.image_url || 'https://images.unsplash.com/photo-1516961642265-531546e84af2?fit=crop&w=800&q=80'} 
                          alt={course.title}
                          className={`w-full h-full object-cover transition-transform duration-500 ${isAvailable ? 'group-hover:scale-105' : 'grayscale-[30%]'}`}
                        />
                        {/* Badge overlay */}
                        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                          <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-[hsl(var(--sage))] text-xs font-medium px-2.5 py-1 rounded-full shadow-sm">
                            <Play className="w-3 h-3" />
                            A TU RITMO
                          </span>
                        </div>
                        {/* Status badge */}
                        {isComingSoon && (
                          <div className="absolute top-3 right-3 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                            Próximamente
                          </div>
                        )}
                        {isNotAvailable && (
                          <div className="absolute top-3 right-3 bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                            No disponible
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="p-5 flex-1 flex flex-col">
                        {/* Meta */}
                        <div className="flex items-center gap-3 mb-3 text-xs text-[hsl(var(--text-tertiary))]">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {course.duration}
                          </span>
                          {course.modules?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-3.5 h-3.5" />
                              {course.modules.length} módulos
                            </span>
                          )}
                        </div>
                        
                        {/* Title */}
                        <h3 className={`text-lg font-heading text-[hsl(var(--text-primary))] mb-1.5 line-clamp-2 ${isAvailable ? 'group-hover:text-[hsl(var(--accent))] transition-colors' : ''}`}>
                          {course.title}
                        </h3>
                        <p className="text-sm text-[hsl(var(--sage))] mb-2">
                          {course.subtitle}
                        </p>
                        <p className="text-sm text-[hsl(var(--text-secondary))] line-clamp-2 mb-4 flex-1">
                          {course.description}
                        </p>
                        
                        {/* Footer */}
                        <div className="pt-4 border-t border-[hsl(var(--border))] flex items-center justify-between">
                          {showPrice ? (
                            <span className="text-xl font-heading text-[hsl(var(--accent))]">
                              {course.price}€
                            </span>
                          ) : (
                            <span className="text-sm font-medium text-[hsl(var(--text-tertiary))] italic">
                              Precio por confirmar
                            </span>
                          )}
                          {isAvailable ? (
                            <span className="text-sm font-medium text-[hsl(var(--accent))] flex items-center gap-1 group-hover:gap-2 transition-all">
                              Empezar ahora
                              <ArrowRight className="w-4 h-4" />
                            </span>
                          ) : isComingSoon ? (
                            <span className="text-sm font-medium text-amber-600">
                              Muy pronto
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">
                              No disponible
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============ RECOMENDADOR ============ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] mb-4">
              ¿No sabes por dónde empezar?
            </h2>
            <p className="text-[hsl(var(--text-secondary))]">
              Encuentra el camino que mejor resuena con tu momento actual
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Path 1: Beginners */}
            <div className="bg-gradient-to-br from-[hsl(var(--sage-light))] to-white rounded-2xl p-6 border border-[hsl(var(--border))] hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--sage))]/20 flex items-center justify-center mb-4">
                <Compass className="w-6 h-6 text-[hsl(var(--sage))]" />
              </div>
              <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2">
                Estoy comenzando mi camino interior
              </h3>
              <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">
                Cursos para sentar bases sólidas de consciencia y presencia.
              </p>
              <p className="text-sm font-medium text-[hsl(var(--accent))]">
                → Consciencia Enraizada<br/>
                → Registros Akáshicos Nivel 1
              </p>
            </div>
            
            {/* Path 2: Healing */}
            <div className="bg-gradient-to-br from-[hsl(var(--sage-light))] to-white rounded-2xl p-6 border border-[hsl(var(--border))] hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--sage))]/20 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-[hsl(var(--sage))]" />
              </div>
              <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2">
                Quiero sanar o regular algo en mi vida
              </h3>
              <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">
                Herramientas para atravesar procesos y cuidar tu energía.
              </p>
              <p className="text-sm font-medium text-[hsl(var(--accent))]">
                → Sanación Energética Consciente<br/>
                → Transiciones Conscientes
              </p>
            </div>
            
            {/* Path 3: Advanced */}
            <div className="bg-gradient-to-br from-[hsl(var(--sage-light))] to-white rounded-2xl p-6 border border-[hsl(var(--border))] hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--sage))]/20 flex items-center justify-center mb-4">
                <Sun className="w-6 h-6 text-[hsl(var(--sage))]" />
              </div>
              <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2">
                Ya tengo experiencia y quiero profundizar
              </h3>
              <p className="text-sm text-[hsl(var(--text-secondary))] mb-4">
                Formaciones avanzadas para integrar y sostener la luz.
              </p>
              <p className="text-sm font-medium text-[hsl(var(--accent))]">
                → Arquitectura de la Luz<br/>
                → Códigos de Luz Nivel 1
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ METODOLOGÍA / CONFIANZA ============ */}
      <section className="py-16 sm:py-20 bg-[hsl(var(--sage-light))]/30">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] mb-4">
              Un enfoque encarnado y regulado
            </h2>
            <p className="text-[hsl(var(--text-secondary))] max-w-2xl mx-auto">
              En esta escuela, la espiritualidad no se piensa: se vive. 
              Cada formación está diseñada para que integres sin forzar, 
              expandas sin desregularte y crezcas sin perderte.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-[hsl(var(--accent))]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2">Consciencia</h3>
              <p className="text-sm text-[hsl(var(--text-secondary))]">
                Aprender a escucharte, leerte y acompañarte desde dentro.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[hsl(var(--accent))]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2">Cuerpo</h3>
              <p className="text-sm text-[hsl(var(--text-secondary))]">
                El cuerpo como criterio de verdad y eje de integración.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-[hsl(var(--accent))]" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-lg text-[hsl(var(--text-primary))] mb-2">Presencia</h3>
              <p className="text-sm text-[hsl(var(--text-secondary))]">
                Habitar la vida real con más claridad y menos ruido.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CIERRE / CTA ============ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading text-[hsl(var(--text-primary))] mb-4">
            Cada camino tiene su momento
          </h2>
          <p className="text-[hsl(var(--text-secondary))] mb-8">
            Si necesitas orientación personal, escríbeme. 
            Te ayudaré a encontrar lo que mejor resuene contigo ahora.
          </p>
          <Link to="/contacto" data-testid="formaciones-contacto">
            <Button className="btn-outline inline-flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Contactar
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Formaciones;
